import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { postAuthorization } from 'services/auth'
import { useAuthorizationContext, useClientContext } from 'contexts'
import { UIButton, UISpacing, UIText, UIAlert, UIFlex } from 'components'
import { useGlobalContext } from 'contexts/global'
import { ErrorPage } from 'pages/ErrorPage'
import { SuccessAuth } from 'pages/SuccessAuth'
import { useNavigate } from 'react-router-dom'
import { TAccessScope, TUserinfo } from 'services/auth/types'
import { sendScreenView, sendSelectContent } from 'utils'
import { FEEDBACK_STATUS } from 'constants/feedback'
import { TError } from 'types'
import { alias } from 'constants/content'

import { interWbNavigate } from '@interco/inter-webview-bridge'

import * as S from './styles'
import { TConsentFinishType } from './types'

export const Consent = () => {
  const { client } = useClientContext()
  const { setHeaderPage, headerPage, content } = useGlobalContext()
  const { authorizationId, consentRequested, requestedClaims, requestedScopes, setRedirectUri } =
    useAuthorizationContext()

  const [consentFinish, setConsentFinish] = useState<TConsentFinishType>(undefined)

  const [loading, setLoading] = useState(false)
  const [errorData, setErrorData] = useState<TError>()

  const navigate = useNavigate()

  const clientAlias = useMemo(() => {
    const aliasKeys = Object.keys(alias)

    if (!aliasKeys.includes(client?.name ?? '')) return undefined

    return alias[client?.name as keyof typeof alias]
  }, [client])

  const handleContinue = useCallback(() => {
    if (authorizationId && client && consentFinish !== 'successAuth' && consentFinish !== 'token') {
      setLoading(true)

      sendSelectContent({
        scenario: client.name,
        step: 'shared_info',
        element_name: 'Confirmar e autorizar',
        element_type: 'button',
        parent_name: 'consent',
      })

      postAuthorization({
        id: authorizationId || '',
        authorizedClaims: requestedClaims,
        authorizedScope: requestedScopes,
      })
        .then((data) => {
          if (!data.otpRequired) {
            setRedirectUri(data.redirectUri || '')
          }

          if (data?.otpChannel) {
            navigate('/token', {
              state: {
                otpChannel: data.otpChannel,
                otpDestination: data.otpDestination,
                otpResendOptions: data.otpResendOptions,
                client: client.name,
              },
            })
            setConsentFinish('token')
          } else {
            setConsentFinish('successAuth')
          }
          setLoading(false)
        })
        .catch((error) => {
          setErrorData({
            scenario: client.name,
            step: 'access_denied',
            error_description: JSON.stringify(error),
            error_message: FEEDBACK_STATUS.error.title,
          })
          setConsentFinish('error')
        })
    } else {
      setErrorData({
        scenario: 'unidentified',
        step: 'access_denied',
        error_description: 'nonexistent authorization_id or unidentified client.',
        error_message: FEEDBACK_STATUS.error.title,
      })
      setConsentFinish('error')
    }
  }, [authorizationId, client, requestedClaims, requestedScopes, navigate, setRedirectUri])

  const handleCancel = useCallback(() => {
    sendSelectContent({
      scenario: client?.name ?? 'unidentified',
      step: 'shared_info',
      element_name: 'Cancelar',
      element_type: 'button',
      parent_name: 'consent',
    })

    interWbNavigate.requestGoBack()
  }, [client])

  useEffect(() => {
    setHeaderPage({ title: content.secondStep.header })
  }, [setHeaderPage, content])

  useEffect(() => {
    if (client && headerPage?.title === content.secondStep.header) {
      sendScreenView({ scenario: client.name, step: 'shared_info' })
    }
  }, [headerPage, client])

  if (consentFinish === 'error') {
    return (
      <ErrorPage
        onContinue={() => interWbNavigate.requestGoBack()}
        errorData={errorData}
        client={client?.name}
      />
    )
  }

  if (consentFinish === 'successAuth') {
    return <SuccessAuth />
  }
  return (
    <S.ConsentContainer>
      <div>
        <UIText variant="headline-h1" semiBold>
          {content.secondStep.headline.replaceAll(':clientName', client?.name || '[cliente]')}
        </UIText>

        <UISpacing mb="md" />

        {consentRequested?.userinfo && consentRequested?.userinfo.length !== 0 && (
          <div>
            <UIAlert type="grayscale" hideIcon>
              <UIText variant="body-2" as="p" color="var(--typography500)" bold>
                {content.secondStep.allowedInfoToShareTitle}
              </UIText>
              <UISpacing mb="xxs" />
              {consentRequested?.userinfo?.map((item: TUserinfo, index: number) => (
                <UIText variant="body-3" as="p" key={item.description}>
                  {item.description}
                  {index < (consentRequested.userinfo?.length || 0) - 1 ? ';' : '.'}
                </UIText>
              ))}
            </UIAlert>
            <UISpacing mb="md" />
          </div>
        )}

        {consentRequested?.accessScope && consentRequested?.accessScope.length !== 0 && (
          <UIAlert type="grayscale" hideIcon>
            <UIText variant="body-2" as="p" color="var(--typography500)" bold>
              {content.secondStep.allowedScopesTitle.replaceAll(
                ':clientName',
                client?.name || '[cliente]',
              )}
            </UIText>
            <UISpacing mb="xxs" />
            {consentRequested?.accessScope?.map((item: TAccessScope, index: number) => (
              <UIText variant="body-3" as="p" key={item.description}>
                {item.description}
                {index < (consentRequested.accessScope?.length || 0) - 1 ? ';' : '.'}
              </UIText>
            ))}
          </UIAlert>
        )}
      </div>

      <UISpacing mb="md" />

      {clientAlias && (
        <UIText variant="body-3">
          {content.secondStep.alertContent.replaceAll(':clientName', clientAlias)}
        </UIText>
      )}

      <UIFlex direction="column" width="100%" alignItems="unset">
        <UIButton variant="primary" onClick={handleContinue} isLoading={loading}>
          {content.secondStep.primaryButtonLabel}
        </UIButton>
        <UISpacing mb="xxs" />
        <UIButton variant="link" onClick={handleCancel} disabled={loading}>
          {content.secondStep.secondaryButtonLabel}
        </UIButton>
      </UIFlex>
    </S.ConsentContainer>
  )
}
